// frontend/src/services/alarmMuteStore.js
//
// Estado de SILENCIO de alarmas, compartido por toda la aplicación y
// persistido por empresa.
//
// Antes vivía en un `useState` del Panel de Control: al salir de esa pantalla el
// silencio se perdía y, en el resto de páginas, el gestor de alarmas arrancaba
// siempre con `globalMute = false`. Resultado: silenciabas y volvía a sonar en
// cuanto navegabas.
//
// IMPORTANTE: silenciar es SOLO sonido. No reconoce, no marca como visto y no
// resuelve tickets — eso son acciones aparte y explícitas.

const STORAGE_KEY = 'tpms_alarm_mute_state';

const emptyState = () => ({ global: false, vehicles: {} });

let companyId = 'default';
let state = emptyState();
const listeners = new Set();

const storageKey = () => `${STORAGE_KEY}:${companyId}`;

const load = () => {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw);
    return {
      global: !!parsed.global,
      vehicles: (parsed.vehicles && typeof parsed.vehicles === 'object') ? parsed.vehicles : {},
    };
  } catch (_) {
    return emptyState();
  }
};

const persist = () => {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(state));
  } catch (_) {
    /* sin localStorage el silencio dura la sesión; no es motivo para fallar */
  }
};

const notify = () => {
  listeners.forEach((listener) => {
    try {
      listener(state);
    } catch (_) {
      /* ignorar suscriptores rotos */
    }
  });
};

/** Cambia el ámbito de empresa: cada empresa tiene su propio silencio. */
export const setMuteCompany = (nextCompanyId) => {
  const next = String(nextCompanyId || 'default');
  if (next === companyId) return;
  companyId = next;
  state = load();
  notify();
};

export const getMuteState = () => state;

export const isVehicleMuted = (vehicleId) =>
  state.global || !!state.vehicles[String(vehicleId)];

export const toggleGlobalMute = () => {
  state = { ...state, global: !state.global };
  persist();
  notify();
  return state;
};

export const setGlobalMute = (value) => {
  state = { ...state, global: !!value };
  persist();
  notify();
  return state;
};

/**
 * Silencia/reactiva un vehículo concreto. Si el silencio general está activo no
 * tiene efecto visible, pero se guarda igual para cuando se desactive.
 */
export const toggleVehicleMute = (vehicleId) => {
  const key = String(vehicleId);
  const vehicles = { ...state.vehicles };
  if (vehicles[key]) {
    delete vehicles[key];
  } else {
    vehicles[key] = true;
  }
  state = { ...state, vehicles };
  persist();
  notify();
  return state;
};

export const clearVehicleMutes = () => {
  state = { ...state, vehicles: {} };
  persist();
  notify();
  return state;
};

export const subscribeMute = (listener) => {
  listeners.add(listener);
  listener(state);
  return () => listeners.delete(listener);
};

// Estado inicial desde almacenamiento.
state = load();

export default {
  setMuteCompany,
  getMuteState,
  isVehicleMuted,
  toggleGlobalMute,
  setGlobalMute,
  toggleVehicleMute,
  clearVehicleMutes,
  subscribeMute,
};
